/**
 * Slot Selection Email step — self-schedule modes only.
 * Full-width single email editor for sending the self-schedule link to the candidate.
 */
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { buildPreviewHtml } from '../communication/buildPreviewHtml';
import { Flex, Text } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import type {
  EmailCompositionValue,
  EmailFieldErrors,
} from '@kula-ai/components';
import type { StepFormApi } from '@/hooks/useStepValidation';
import { useUploadUserInlineImage } from '@/api/userImageUploads';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useInterviewEmailTemplateOptions } from '../../../InterviewActivity/communication/api';
import { useInterviewActivityCommunicationVariables } from '../../../InterviewActivity/communication/api';
import type { InterviewEditorAttachmentItem } from '../../../InterviewActivity/communication/types';
import {
  emailCompositionToCommConfig,
  attachEditorItemsToCommConfig,
  patchEmailComposition,
  getDefaultEmailComposition,
} from '../../../InterviewActivity/communication/helpers/emailUtils';
import { compositionFromTemplateOption } from '../../../InterviewActivity/communication/helpers/templateApply';
import { TemplatedEmail } from '../../../components/TemplatedEmail';
import {
  parseSharedEmailId,
  resolveDefaultFromValueForCurrentUser,
  useScheduleSenderOptions,
} from '../../hooks/useScheduleSenderOptions';
import { SCHEDULE_INTERVIEW_COPY } from '../../config/copy';
import type { ScheduleStepProps } from '../../config/types';
import {
  REQUIRED_SELF_SCHEDULE_VARIABLE,
  validateSlotSelectionEmail,
} from './slotSelectionEmail.validate';

export const SlotSelectionEmailStep = memo(
  forwardRef<
    StepFormApi,
    ScheduleStepProps & { candidateName?: string; jobTitle?: string }
  >(
    (
      {
        payloadRef,
        mode: _mode,
        apiReference: _apiReference,
        prefillVersion = 0,
        candidateEmail = '',
        bulkSelfScheduleApplicationCount,
        candidateName,
        jobTitle,
      },
      ref
    ) => {
      // Bulk self-schedule with >1 candidate has no single "To" address
      // — the backend derives recipients from `ats_application_ids[]`.
      // Hiding the row prevents users from thinking they can edit it
      // and avoids the lazy-init / sync-effect pair below trying to
      // backfill a value that has no meaning here.
      const hideToField = (bulkSelfScheduleApplicationCount ?? 0) > 1;
      const copy = SCHEDULE_INTERVIEW_COPY.slotSelectionEmail;

      // Only the candidate receives this email, so the `To` picker is
      // a single-option select whose one entry is the candidate's
      // email. Keep the value stable across renders.
      const toOptions = useMemo(() => {
        if (!candidateEmail) return [];
        return [
          {
            value: candidateEmail,
            label: candidateEmail,
          },
        ];
      }, [candidateEmail]);

      const currentUser = useSelector(selectCurrUser) as
        | {
            id?: number | string;
            first_name?: string;
            last_name?: string;
            email?: string;
          }
        | undefined;

      // ---- Preview state ----
      const [previewHtml, setPreviewHtml] = useState<string | undefined>(
        undefined
      );

      // ---- Data fetching ----
      const { data: templateOptions = [] } =
        useInterviewEmailTemplateOptions('self-schedule');
      // Slot-selection email opts into shared-mailbox sender options
      // — `mapSlotEmail` knows how to route them via
      // `candidate_mail_ats_shared_email_id`. The comm step
      // (`CommunicationStep`) does NOT opt in: its CORE-4040 nested
      // configs only accept numeric user ids on the backend permit
      // list today, so a `shared:${id}` form value would silently
      // drop. See `useScheduleSenderOptions` JSDoc.
      const {
        fromOptions,
        ccBccOptions,
        isFromOptionsFetched,
        isFromOptionsLoading,
      } = useScheduleSenderOptions({ includeSharedMailboxes: true });

      // The default `from` is the current user — but only if they're
      // actually delegatable. Without this check, signing in as a user
      // whose mailbox is admin-only set `from = <their id>`, the Select
      // saw no matching option, rendered empty, and the validator
      // claimed the form was valid. Empty string now falls through to
      // the required-field error so the user is forced to pick.
      const defaultFromValue = useMemo(
        () =>
          resolveDefaultFromValueForCurrentUser(fromOptions, currentUser?.id),
        [fromOptions, currentUser]
      );

      // ---- Email state ----
      const [value, setValue] = useState<EmailCompositionValue>(() => {
        const existing = payloadRef.current.slotSelectionEmail;
        if (existing && typeof existing === 'object' && 'content' in existing) {
          const hydrated = existing as unknown as EmailCompositionValue;
          const patched: Partial<EmailCompositionValue> = {};
          if (!hydrated.to && candidateEmail) patched.to = candidateEmail;
          if (!hydrated.from) patched.from = defaultFromValue;
          return Object.keys(patched).length > 0
            ? { ...hydrated, ...patched }
            : hydrated;
        }
        return patchEmailComposition(getDefaultEmailComposition(), {
          from: defaultFromValue,
          to: candidateEmail,
        });
      });
      const [attachments, setAttachments] = useState<
        InterviewEditorAttachmentItem[]
      >([]);
      const [fieldErrors, setFieldErrors] = useState<
        EmailFieldErrors | undefined
      >();

      const { selfScheduleEmail: selfScheduleVariables } =
        useInterviewActivityCommunicationVariables();

      const { mutateAsync: uploadUserInlineImageAsync } =
        useUploadUserInlineImage();
      const uploadInlineImage = useCallback(
        (file: File) => uploadUserInlineImageAsync(file),
        [uploadUserInlineImageAsync]
      );

      // Guarantee `to` tracks `candidateEmail` once it resolves. The
      // lazy init above runs before `applicationData.contact` may
      // arrive (async `/ats_applications/:id` fetch) and there's no
      // user-editable `To` picker, so this is a pure defensive sync.
      // Bulk multi-candidate has no single recipient — backend derives
      // them from `ats_application_ids[]` server-side — so skip the
      // sync rather than seeding a meaningless address.
      useEffect(() => {
        if (hideToField) return;
        if (!candidateEmail) return;
        if (value.to === candidateEmail) return;
        setValue((prev) => ({ ...prev, to: candidateEmail }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [candidateEmail, hideToField]);

      // Backfill `from` once both the delegatable-senders fetch AND
      // Redux `currUser` have settled. Waiting for `isFromOptionsFetched`
      // is what prevents the "id set but not in options" bug — the
      // resolver returns '' for a non-delegatable user, leaving the
      // select empty so the required-field validator can catch it.
      useEffect(() => {
        if (!isFromOptionsFetched) return;
        if (value.from) return;
        if (!defaultFromValue) return;
        setValue((prev) => ({ ...prev, from: defaultFromValue }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [defaultFromValue, isFromOptionsFetched]);

      // ---- One-shot template default (create mode only) ----
      const didDefaultTemplate = useRef(false);

      useEffect(() => {
        if (prefillVersion > 0 || !templateOptions.length) return;
        if (value.template) {
          didDefaultTemplate.current = true;
          return;
        }
        if (didDefaultTemplate.current) return;
        didDefaultTemplate.current = true;
        setValue(compositionFromTemplateOption(value, templateOptions[0]));
      }, [prefillVersion, templateOptions, value]);

      // ---- Live validation after first failure ----
      useEffect(() => {
        setFieldErrors((prev) => {
          if (!prev) return prev;
          return validateSlotSelectionEmail(value, value.from, {
            skipToCheck: hideToField,
          });
        });
      }, [value, hideToField]);

      // ---- Imperative handle ----
      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            const errors = validateSlotSelectionEmail(value, value.from, {
              skipToCheck: hideToField,
            });
            setFieldErrors(errors);
            return !errors;
          },
          syncToPayload: () => {
            // Shared-mailbox sender: form stores `from` as
            // `shared:${id}` (see `useScheduleSenderOptions`).
            // `emailCompositionToCommConfig` only knows numeric user
            // ids, so substitute the current user's id as the
            // apparent `from` and stash the shared mailbox id under a
            // UI-only key for `mapSlotEmail` to pick up. This keeps
            // the InterviewActivity helper untouched (it's shared
            // with the calendar-invite step).
            const sharedEmailId = parseSharedEmailId(value.from);
            const valueForCompose =
              sharedEmailId !== undefined && currentUser?.id !== undefined
                ? { ...value, from: String(currentUser.id) }
                : value;
            const config = attachEditorItemsToCommConfig(
              emailCompositionToCommConfig(valueForCompose),
              attachments
            );
            payloadRef.current.slotSelectionEmail = {
              ...config,
              _ui_template_id: value.template ?? null,
              ...(sharedEmailId !== undefined
                ? { _shared_email_id: sharedEmailId }
                : {}),
            } as unknown as Record<string, unknown>;
          },
        }),
        [attachments, currentUser?.id, hideToField, payloadRef, value]
      );

      const buildSlotEmailVars = useCallback((): Record<string, string> => {
        const parts = candidateName
          ? candidateName.trim().split(/\s+/)
          : undefined;
        const firstName = parts?.[0];
        const lastName =
          parts && parts.length > 1 ? parts.slice(1).join(' ') : undefined;
        const senderFirst = currentUser?.first_name;
        const senderLast = currentUser?.last_name;
        const senderFull =
          senderFirst || senderLast
            ? `${senderFirst ?? ''} ${senderLast ?? ''}`.trim()
            : undefined;

        const vars: Record<string, string> = {};
        const set = (key: string, val: string | undefined) => {
          if (val !== undefined && val !== '') vars[`{{${key}}}`] = val;
        };
        set('candidate.first_name', firstName);
        set('candidate.last_name', lastName);
        set('candidate.full_name', candidateName?.trim());
        set('job.title', jobTitle);
        set('sender.first_name', senderFirst);
        set('sender.last_name', senderLast);
        set('sender.full_name', senderFull);
        set('sender.email', currentUser?.email);
        return vars;
      }, [candidateName, currentUser, jobTitle]);

      // ---- Render ----
      return (
        <Flex
          key={String(prefillVersion)}
          w="100%"
          h="100%"
          minH={0}
          flex="1"
          justifyContent="center"
          overflow="auto"
          pb="8"
        >
          <Flex w="100%" maxW="720px" flexDir="column">
            <TemplatedEmail
              className="w-full"
              showFrom
              showTo={!hideToField}
              showSubject
              enableCcBcc
              showTemplate
              showEditor
              editorVariant="richEmail"
              variables={selfScheduleVariables}
              attachments={attachments}
              onAttachmentsChange={setAttachments}
              templateOptions={templateOptions}
              fromOptions={fromOptions}
              fromSelectProps={{ loading: isFromOptionsLoading }}
              toOptions={toOptions}
              toMode="single"
              toSelectProps={{ disabled: toOptions.length <= 1 }}
              ccBccOptions={ccBccOptions}
              applyTemplateBodyToEditor
              value={value}
              onChange={setValue}
              errors={fieldErrors}
              placeholders={{
                from: copy.placeholders.from,
                to: copy.placeholders.to,
                subject: copy.placeholders.subject,
                template: copy.placeholders.template,
              }}
              uploadInlineImage={uploadInlineImage}
              editorProps={{
                previewHtml,
                onPreviewClick: () =>
                  setPreviewHtml(
                    buildPreviewHtml(value.content, buildSlotEmailVars())
                  ),
                onPreviewDismiss: () => setPreviewHtml(undefined),
              }}
            />
            {/* Red hint surfaces only when the validator flagged the
                missing merge tag. The validator writes a specific
                `content` error message that includes the variable
                name, so we key off that rather than introspecting
                the editor body separately. */}
            {fieldErrors?.content?.includes(
              `{{${REQUIRED_SELF_SCHEDULE_VARIABLE}}}`
            ) ? (
              <Text mt="2" fontSize="xs" color="error-200">
                Required variable:{' '}
                <strong>{`{{${REQUIRED_SELF_SCHEDULE_VARIABLE}}}`}</strong>
              </Text>
            ) : null}
          </Flex>
        </Flex>
      );
    }
  )
);

SlotSelectionEmailStep.displayName = 'SlotSelectionEmailStep';
